import type { MboxHistoryView } from "./api";
import type { MboxHistoryLoadState } from "./useMboxHistory";
import {
  archiveGradeLabel,
  formatMoment,
  organizationTypeLabel,
  transactionStatusLabel,
} from "./mbox-display";

/**
 * Customer-dimension archive facts, folded into the existing customer profile.
 *
 * Nothing is duplicated: the archive only contributes a footnote under 机构名称 (registered
 * domain, sub-domains, organization type), a one-line grade suggestion under 客户等级, and one
 * row inside 历史记录 next to the CRM workspace counts.
 */
export function archiveGradeSuggestion(
  state: MboxHistoryLoadState,
  currentGrade?: string | null,
): { text: string; conflicts: boolean } | null {
  const company = readyCompany(state);
  if (!company || company.rating.grade === "UNKNOWN") return null;
  const label = archiveGradeLabel(company.rating.grade);
  // 历史库是我方内部留证、未经人工复核，等级不一致时交给分诊员判断，所以只强调冲突这一种情况。
  const conflicts =
    Boolean(currentGrade) &&
    currentGrade !== "PENDING" &&
    company.rating.grade !== currentGrade;
  return {
    text: conflicts ? `历史建议：${label}（与当前不符）` : `历史建议：${label}`,
    conflicts,
  };
}

export function archiveDomainNote(state: MboxHistoryLoadState): string | null {
  const company = readyCompany(state);
  if (!company) return null;
  const parts = [
    company.canonicalDomain,
    company.observedDomains.length > 1
      ? `含 ${company.observedDomains.length} 个子域名`
      : null,
    organizationTypeLabel[company.organizationType] ?? company.organizationType,
  ].filter((part): part is string => Boolean(part));
  return `历史邮件库：${parts.join(" · ")}`;
}

/**
 * Merged customer history: the CRM workspace row always renders, the archive row explains its
 * own availability instead of repeating the same counts under a second heading.
 */
export function CustomerHistoryBlock({
  state,
  inquiryCount,
  orderCount,
  onOpenHistory,
}: {
  state: MboxHistoryLoadState;
  inquiryCount: number;
  orderCount: number;
  onOpenHistory: () => void;
}) {
  const archiveRow = archiveHistoryRow(state);
  return (
    <div className="real-customer-history">
      <span className="real-customer-history__title">历史记录</span>
      <div className="real-customer-history__row">
        <span className="real-customer-history__source">CRM 工作空间</span>
        <span className="real-customer-history__values">
          <strong>{inquiryCount}</strong> 询单
          <em>·</em>
          <strong>{orderCount}</strong> 订单
        </span>
        <button type="button" onClick={onOpenHistory}>
          查看历史记录
        </button>
      </div>
      {archiveRow ? (
        <div className="real-customer-history__row">
          <span className="real-customer-history__source">历史邮件库</span>
          {archiveRow.values ? (
            <span className="real-customer-history__values">
              <strong>{archiveRow.values.inquiries}</strong> 询单
              <em>·</em>
              <strong>{archiveRow.values.orders}</strong> 订单 / PO 邮件
              <em>·</em>
              <strong>{archiveRow.values.transactions}</strong> 笔成交
            </span>
          ) : (
            <span className="real-customer-history__values is-muted">
              {archiveRow.placeholder}
            </span>
          )}
        </div>
      ) : null}
      {archiveRow?.note ? (
        <p className="real-customer-history__note">{archiveRow.note}</p>
      ) : null}
    </div>
  );
}

function readyCompany(state: MboxHistoryLoadState) {
  if (state.status !== "READY") return null;
  if (state.view.status === "DISABLED" || state.view.status === "UNAVAILABLE")
    return null;
  return state.view.company;
}

function archiveHistoryRow(state: MboxHistoryLoadState): {
  readonly values: {
    readonly inquiries: number;
    readonly orders: number;
    readonly transactions: number;
  } | null;
  readonly placeholder: string | null;
  readonly note: string | null;
} | null {
  if (state.status === "UNSUPPORTED") return null;
  if (state.status === "LOADING") {
    return { values: null, placeholder: "查询中…", note: null };
  }
  if (state.status === "FAILED") {
    return { values: null, placeholder: "暂时无法查询", note: null };
  }
  const view: MboxHistoryView = state.view;
  // No archive configured: the CRM row stands alone rather than advertising an absent source.
  if (view.status === "DISABLED") return null;
  if (view.status === "UNAVAILABLE") {
    return { values: null, placeholder: "暂时无法查询", note: null };
  }
  const company = view.company;
  if (!company) {
    return { values: null, placeholder: "该域名无历史邮件记录", note: null };
  }
  return {
    values: {
      inquiries: company.inquiries.count,
      orders: company.inquiries.orderOrPoMessageCount,
      transactions: company.transactions.historicalCount,
    },
    placeholder: null,
    note: archiveRecencyNote(company),
  };
}

function archiveRecencyNote(
  company: NonNullable<MboxHistoryView["company"]>,
): string | null {
  const latest = company.transactions.recent[0];
  const parts = [
    company.inquiries.lastAt
      ? `最近询单 ${formatMoment(company.inquiries.lastAt)}`
      : null,
    latest
      ? `最近成交 ${latest.orderIdentifier ?? "无订单号"} · ${
          latest.status
            ? (transactionStatusLabel[latest.status] ?? latest.status)
            : "状态未记录"
        } · ${formatMoment(latest.transactionAt)}`
      : null,
    "按域名归并统计",
  ].filter((part): part is string => Boolean(part));
  return parts.length > 0 ? parts.join(" · ") : null;
}
